import React, { useState, useEffect } from 'react';
import { DocumentTextIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

const Notes = () => {
  const [markdownContent, setMarkdownContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tableOfContents, setTableOfContents] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    const fetchMarkdownContent = async () => {
      try {
        setLoading(true);
        // Fetch the markdown file from the API endpoint
        const response = await fetch('/api/v1/dev-notes');
        if (!response.ok) {
          throw new Error('Failed to fetch markdown file');
        }
        const content = await response.text();
        setMarkdownContent(content);
        
        // Extract table of contents
        const toc = extractTableOfContents(content);
        setTableOfContents(toc);
        
        // Set initial active section from URL hash
        const hash = window.location.hash.substring(1);
        if (hash) {
          setActiveSection(hash);
          // Scroll to the section after a short delay to ensure content is rendered
          setTimeout(() => {
            const element = document.getElementById(hash);
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }, 100);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchMarkdownContent();
  }, []);

  // Scroll spy to track active section
  useEffect(() => {
    if (!tableOfContents.length) return;

    const handleScroll = () => {
      const sections = tableOfContents.map(item => ({
        id: item.id,
        element: document.getElementById(item.id)
      })).filter(item => item.element);

      // Find the section that's currently in view
      let currentSection = '';
      for (const section of sections) {
        const rect = section.element.getBoundingClientRect();
        // Check if the section is visible (accounting for fixed header)
        if (rect.top <= 150 && rect.bottom > 150) {
          currentSection = section.id;
          break;
        }
      }

      // If no section is in the middle, use the first visible one
      if (!currentSection) {
        for (const section of sections) {
          const rect = section.element.getBoundingClientRect();
          if (rect.top <= window.innerHeight && rect.bottom >= 0) {
            currentSection = section.id;
            break;
          }
        }
      }

      if (currentSection && currentSection !== activeSection) {
        setActiveSection(currentSection);
        // Update URL hash without scrolling
        if (window.location.hash !== `#${currentSection}`) {
          window.history.replaceState(null, null, `#${currentSection}`);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    // Run once to set initial state
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, [tableOfContents, activeSection]);

  // Handle hash changes (browser back/forward)
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.substring(1);
      if (hash && hash !== activeSection) {
        setActiveSection(hash);
        const element = document.getElementById(hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [activeSection]);

  // Extract headers for table of contents
  const extractTableOfContents = (markdown) => {
    const headerRegex = /^(#{1,3})\s+(.*$)/gim;
    const headers = [];
    let match;
    
    while ((match = headerRegex.exec(markdown)) !== null) {
      const level = match[1].length;
      const title = match[2].trim();
      const id = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      
      headers.push({
        level,
        title,
        id
      });
    }
    
    return headers;
  };

  // Simple markdown to HTML converter for basic formatting
  const parseMarkdown = (markdown) => {
    // Helper function to create header ID
    const createHeaderId = (title) => {
      return title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    };

    let html = markdown
      // Process paragraphs first (split by double newlines)
      .split('\n\n')
      .map(paragraph => {
        // Skip if it's a header, list item, or code block
        if (paragraph.match(/^#{1,3}\s+|^\-\s+|^```/)) {
          return paragraph;
        }
        // Wrap regular paragraphs
        return paragraph.trim() ? `<p class="mb-4">${paragraph}</p>` : '';
      })
      .join('\n\n')
      
      // Headers with IDs (process in order from largest to smallest)
      .replace(/^### (.*$)/gim, (match, title) => {
        const id = createHeaderId(title);
        return `<h3 id="${id}" class="text-lg font-semibold text-gray-900 mt-6 mb-3 scroll-mt-20">${title}</h3>`;
      })
      .replace(/^## (.*$)/gim, (match, title) => {
        const id = createHeaderId(title);
        return `<h2 id="${id}" class="text-xl font-semibold text-gray-900 mt-8 mb-4 border-b border-gray-200 pb-2 scroll-mt-20">${title}</h2>`;
      })
      .replace(/^# (.*$)/gim, (match, title) => {
        const id = createHeaderId(title);
        return `<h1 id="${id}" class="text-2xl font-bold text-gray-900 mt-8 mb-6 border-b-2 border-gray-300 pb-3 scroll-mt-20">${title}</h1>`;
      })
      
      // Code blocks (with syntax highlighting classes)
      .replace(/```javascript\n([\s\S]*?)\n```/g, '<pre class="bg-gray-800 text-green-400 border border-gray-600 rounded-md p-4 my-4 overflow-x-auto"><code class="text-sm whitespace-pre-wrap">$1</code></pre>')
      .replace(/```graphql\n([\s\S]*?)\n```/g, '<pre class="bg-gray-800 text-blue-400 border border-gray-600 rounded-md p-4 my-4 overflow-x-auto"><code class="text-sm whitespace-pre-wrap">$1</code></pre>')
      .replace(/```\n([\s\S]*?)\n```/g, '<pre class="bg-gray-100 border border-gray-300 rounded-md p-4 my-4 overflow-x-auto"><code class="text-sm text-gray-800 whitespace-pre-wrap">$1</code></pre>')
      
      // Inline code
      .replace(/`([^`]+)`/g, '<code class="bg-gray-100 text-red-600 px-2 py-1 rounded text-sm font-mono">$1</code>')
      
      // Bold text with special handling for warnings
      .replace(/\*\*⚠️ IMPORTANT\*\*: (.*?)$/gim, '<div class="bg-yellow-50 border-l-4 border-yellow-400 p-4 my-4"><div class="flex"><div class="flex-shrink-0"><span class="text-yellow-400 text-lg">⚠️</span></div><div class="ml-3"><p class="text-sm"><strong class="font-semibold text-yellow-800">IMPORTANT:</strong> <span class="text-yellow-700">$1</span></p></div></div></div>')
      .replace(/\*\*(.*?)\*\*/g, '<strong class="font-semibold text-gray-900">$1</strong>')
      
      // Lists (wrap in ul and handle li)
      .replace(/^- (.*$)/gim, '<li class="mb-1">$1</li>')
      
      // Links
      .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-blue-600 hover:text-blue-800 underline font-medium" target="_blank" rel="noopener noreferrer">$1</a>')
      
      // Horizontal rules
      .replace(/^---$/gim, '<hr class="border-t border-gray-300 my-8" />')
      
      // Wrap consecutive list items in ul tags
      .replace(/(<li class="mb-1">.*<\/li>)(\s*<li class="mb-1">.*<\/li>)*/g, (match) => {
        return `<ul class="list-disc ml-6 mb-4">${match}</ul>`;
      })
      
      // Clean up double paragraph tags
      .replace(/<p class="mb-4"><\/p>/g, '')
      
      // Single line breaks
      .replace(/\n/g, '<br />');

    return html;
  };

  // Highlight search terms in the content
  const highlightSearchTerms = (html, searchTerm) => {
    if (!searchTerm.trim()) return html;
    
    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    return html.replace(regex, '<mark class="bg-yellow-200 text-yellow-900 font-medium">$1</mark>');
  };

  if (loading) {
    return (
      <div className="w-full h-full px-4 sm:px-6 lg:px-8">
        <div className="py-6">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <DocumentTextIcon className="h-8 w-8 text-gray-600" />
              <h1 className="text-3xl font-bold text-gray-900">App Notes</h1>
            </div>
            
            {/* Search Box - disabled during loading */}
            <div className="relative w-64">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-gray-100 placeholder-gray-500 cursor-not-allowed"
                placeholder="Loading..."
                disabled
              />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">
                Developer Documentation & System Context
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Loading documentation...
              </p>
            </div>
            <div className="px-6 py-6">
              <div className="max-w-4xl mx-auto">
                <div className="animate-pulse" style={{ maxWidth: '800px' }}>
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded w-5/6 mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded w-2/3 mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded w-4/5 mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full h-full px-4 sm:px-6 lg:px-8">
        <div className="py-6">
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-3">
              <DocumentTextIcon className="h-8 w-8 text-gray-600" />
              <h1 className="text-3xl font-bold text-gray-900">App Notes</h1>
            </div>
            
            {/* Search Box - disabled during error */}
            <div className="relative w-64">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-gray-100 placeholder-gray-500 cursor-not-allowed"
                placeholder="Error loading..."
                disabled
              />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">
                Developer Documentation & System Context
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Error loading documentation
              </p>
            </div>
            <div className="px-6 py-6">
              <div className="max-w-4xl mx-auto">
                <div className="text-center" style={{ maxWidth: '800px' }}>
                  <div className="text-red-600 text-lg font-medium mb-2">Error Loading Notes</div>
                  <p className="text-gray-600">{error}</p>
                  <p className="text-sm text-gray-500 mt-4">
                    Please check that the DevNotes.md file exists in the Instructions/ directory.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full px-4 sm:px-6 lg:px-8">
      <div className="py-6">
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-3">
            <DocumentTextIcon className="h-8 w-8 text-gray-600" />
            <h1 className="text-3xl font-bold text-gray-900">App Notes</h1>
          </div>
          
          {/* Search Box */}
          <div className="relative w-64">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              placeholder="Search documentation..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        
        <div className="flex gap-6 relative">
          {/* Table of Contents - Fixed Position */}
          {tableOfContents.length > 0 && (
            <div className="hidden lg:block w-64 flex-shrink-0">
              <div className="fixed top-24 w-64 bg-white rounded-lg shadow border border-gray-200 max-h-[calc(100vh-8rem)] overflow-hidden">
                <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                  <h3 className="text-sm font-medium text-gray-900">Table of Contents</h3>
                </div>
                <nav className="px-4 py-3 overflow-y-auto max-h-[calc(100vh-12rem)]">
                  <ul className="space-y-1">
                    {tableOfContents.map((item, index) => (
                      <li key={index}>
                        <a
                          href={`#${item.id}`}
                          className={`block text-sm transition-colors py-1 hover:text-blue-600 rounded ${
                            activeSection === item.id
                              ? `text-blue-600 font-medium bg-blue-50 border-r-2 border-blue-600 pr-2 ${
                                  item.level === 2 ? 'ml-3' : item.level === 3 ? 'ml-6' : ''
                                }`
                              : item.level === 1 
                                ? 'font-medium text-gray-900' 
                                : item.level === 2 
                                  ? 'text-gray-700 ml-3' 
                                  : 'text-gray-600 ml-6'
                          }`}
                          onClick={(e) => {
                            e.preventDefault();
                            const element = document.getElementById(item.id);
                            if (element) {
                              setActiveSection(item.id);
                              window.history.pushState(null, null, `#${item.id}`);
                              element.scrollIntoView({ behavior: 'smooth', block: 'start' });
                            }
                          }}
                        >
                          {item.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav>
              </div>
            </div>
          )}
          
          {/* Main Content with Max Width */}
          <div className="flex-1 bg-white rounded-lg shadow ml-0 lg:ml-72">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-medium text-gray-900">
                Developer Documentation & System Context
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Comprehensive documentation for the Eadin.io ERP system
              </p>
            </div>
            
            <div className="px-6 py-6">
              <div className="max-w-4xl mx-auto">
                <div 
                  className="prose prose-sm max-w-none"
                  style={{ maxWidth: '800px' }}
                  dangerouslySetInnerHTML={{ 
                    __html: highlightSearchTerms(parseMarkdown(markdownContent), searchTerm)
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Notes; 